import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

/*
  Shared layout for legal pages (Privacy Policy, Terms of Use, Cookie Policy).
  Renders a calm editorial article with a sticky table of contents on desktop.

  sections: [{ id, title, body: [ { type: 'p', text } | { type: 'ul', items: [] } ] }]
*/

const renderBodyItem = (item, idx) => {
  if (item.type === "ul") {
    return (
      <ul key={idx} className="mt-4 list-disc space-y-2 pl-6 text-sm leading-relaxed text-muted-foreground sm:text-base">
        {item.items.map((li, i) => (
          <li key={i}>{li}</li>
        ))}
      </ul>
    );
  }
  return (
    <p key={idx} className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
      {item.text}
    </p>
  );
};

export const LegalLayout = ({ title, lastUpdated, intro, sections, testId }) => {
  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 88;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="wfii-longform flex-1" data-testid={testId}>
        {/* Page header */}
        <div className="border-b border-border bg-[hsl(var(--wfii-cool-200))]">
          <div className="mx-auto w-full max-w-[1288px] px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Legal
            </p>
            <h1 className="mt-3 text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl" data-testid="legal-page-title">
              {title}
            </h1>
            <p className="mt-4 text-sm text-muted-foreground" data-testid="legal-page-last-updated">
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="mx-auto w-full max-w-[1288px] px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            {/* Table of contents */}
            <aside className="hidden lg:col-span-4 lg:block">
              <nav className="sticky top-24 rounded-xl border border-border bg-card p-6" aria-label="Table of contents" data-testid="legal-toc">
                <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  On this page
                </h2>
                <ol className="mt-4 space-y-2.5">
                  {sections.map((s, i) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        onClick={(e) => handleTocClick(e, s.id)}
                        className="block text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                        data-testid={`legal-toc-link-${s.id}`}
                      >
                        {i + 1}. {s.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </aside>

            {/* Article */}
            <article className="lg:col-span-8">
              {intro && (
                <div className="border-b border-border pb-8">
                  {intro.map((item, idx) => renderBodyItem(item, idx))}
                </div>
              )}
              {sections.map((s, i) => (
                <section key={s.id} id={s.id} className="scroll-mt-24 border-b border-border py-8 last:border-b-0" data-testid={`legal-section-${s.id}`}>
                  <h2 className="text-xl font-semibold tracking-[-0.01em] text-foreground sm:text-2xl">
                    {i + 1}. {s.title}
                  </h2>
                  {s.body.map((item, idx) => renderBodyItem(item, idx))}
                </section>
              ))}
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
